import React, { useState, useCallback, useContext } from 'react';
import styled, { keyframes } from 'styled-components';
import ShownPopupsContext from '../../contexts/popups_context';
import ContextAwarePopup from './detail/context_aware_popup';
import { POPUP_BACKOFF_SCHEDULE } from './detail/general_params';
import Media from 'react-media';
import L from '../common/localized';
import FlexWrapper from '../common/flex';
import I18nContext from '../../contexts/i18n';
import ExtLink from '../common/external_link';
import { isEnvironmentIOS } from '../../utils/os/ios';
import { isEnvironmentAndroid } from '../../utils/os/android';

export const POPUP_TAG = 'download-app';
const APP_DOWNLOAD_LINK = 'https://lemonadefashion.app.link/LF-website-toast';

const DownloadAppPopup = () => {
  const [showing, setShowing] = useState(false);
  const onClose = useCallback(() => setShowing(false), []);
  const shownPopupsContainer = useContext(ShownPopupsContext);
  const i18nSettings = useContext(I18nContext);
  const rtl = i18nSettings.isLocaleRTL();
  // If the user go to the download page, then no action is needed in the future.
  const isIos = isEnvironmentIOS();
  const isAndroid = isEnvironmentAndroid();
  const onDownload = useCallback(() => {
    shownPopupsContainer.markAsNoFurtherActionNeeded(POPUP_TAG);
  }, [shownPopupsContainer]);

  return (
    <Media query='(min-width: 768px)'>
      {isDesktop =>
        (isAndroid || isIos) &&
        <ContextAwarePopup showing={showing} setShowing={setShowing} tag={POPUP_TAG}
          shownPopupsContainer={shownPopupsContainer} backoffPeriodSchedule={POPUP_BACKOFF_SCHEDULE}
          minimumDelayToShow={5000} shouldShowAutomatically minimumScrollToShow={0}>
          <NotificationWrapper dir={rtl ? 'rtl' : 'ltr'}>
            <NotificationToastWrapper justifyContent='space-between' dir={rtl ? 'rtl' : 'ltr'} vertical={!isDesktop}
              alignItems={!isDesktop && 'flex-start'} desktop={isDesktop}>
              <TitleText>
                <L text={'It\'s better in the app'} />
              </TitleText>
              <ActionContainer justifyContent={isDesktop ? 'space-around' : 'flex-end'} desktop={isDesktop}>
                <ActionText onClick={onClose}>
                  <L text='Later' />
                </ActionText>
                <ActionText onClick={onDownload}>
                  <ModifiedExtLink to={APP_DOWNLOAD_LINK}>
                    <L text='Download The App' />
                  </ModifiedExtLink>
                </ActionText>
              </ActionContainer>
            </NotificationToastWrapper>
          </NotificationWrapper>
        </ContextAwarePopup>}
    </Media>
  );
};

const inRightAnimation = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0);}
`;

const NotificationWrapper = styled.div`
  box-sizing: border-box;
  position: fixed;
  z-index: 999999;
  bottom: 16px;
  transition: transform .6s ease-in-out;
  animation: ${inRightAnimation} .7s;
  ${props => (props.dir === 'rtl') && 'left: 16px;'};
  ${props => (props.dir !== 'rtl') && 'right: 16px;'};
`;

const NotificationToastWrapper = styled(FlexWrapper)`
  background: #fff;
  transition: .4s ease;
  position: relative;
  overflow: hidden;
  margin: 0 0 6px;
  padding: ${props => props.desktop ? '24px' : '12px 24px'};
  height: 80px;
  width: 550px;
  max-width: calc(100vw - 32px);
  box-shadow: 0 2px 1px rgba(0,0,0,0.09),
              0 4px 2px rgba(0,0,0,0.09),
              0 8px 4px rgba(0,0,0,0.09),
              0 16px 8px rgba(0,0,0,0.09),
              0 32px 16px rgba(0,0,0,0.09);
`;

const ActionContainer = styled(FlexWrapper)`
  width: ${props => props.desktop ? '40%' : '100%'};
  ${props => !props.desktop &&
    `>p:first-child {
    margin-right: 8px;
  }`
  }
`;

const ActionText = styled.p`
  color: #1d4ed8;
  font-weight: 600;
  font-size: 14px;
  line-height: 21px;
`;

const TitleText = styled.p`
  font-weight: 400;
  font-size: 14px;
  line-height: 21px;
  color: #333333;
`;

const ModifiedExtLink = styled(ExtLink)`
  &:visited {
    color: #1d4ed8;
    cursor: pointer;
  }
`;

export default DownloadAppPopup;
